import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import {
    IAttendeeBulkUpdateDTO,
    IAttendeeDetailsDTO,
    IAttendeesDAO,
    IAttendeesListDTO,
    ICreateAttendeeDTO,
    IDeleteAttendeeDTO,
    TGetAttendeesSortKeys
} from '../attendees.dao';
import {
    IEventRequestParams,
    IFileParams,
    IPaginationParams,
    ISortRequestParams,
    replaceSortRequestParamsWithSortString
} from '../shared.dto';
import * as ATTENDEE_API from '../../constants/api/attendee-api.constants';
import { Permission } from '../roles.dao';
import { JobDtoType } from '../../domain/jobs';

class AttendeesDAO extends IAttendeesDAO {
    constructor(@IoC.Inject private readonly apiClient: IApiClient) {
        super();
    }

    public async getAttendees(params: IEventRequestParams & ISortRequestParams<TGetAttendeesSortKeys> & IPaginationParams): Promise<IAttendeesListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        const response = await this.apiClient.get<IAttendeesListDTO>(ATTENDEE_API.ATTENDEES_LIST_URL, paramsWithSortString);
        return response.parsedResponse;
    }

    public async getAttendee(params: IEventRequestParams & { attendeeId: number}): Promise<IAttendeeDetailsDTO> {
        const response = await this.apiClient.get<IAttendeeDetailsDTO>(ATTENDEE_API.GET_ATTENDEE_URL, params);
        return response.parsedResponse;
    }

    public async getAttendeesCSV(params: IEventRequestParams) {
        await this.apiClient.getFile(ATTENDEE_API.CSV_DOWNLOAD_URL, params);
    }

    public async updateAttendee(params: IAttendeeDetailsDTO & IEventRequestParams): Promise<IAttendeeDetailsDTO> {
        const response = await this.apiClient.put<IAttendeeDetailsDTO>(
            {
                url: ATTENDEE_API.UPDATE_ATTENDEE_URL,
                permission: Permission.UPDATE_ATTENDEE
            },
            params,
            {
                uriParams: 'sendRegistrationEmail' in params ? {
                    sendRegistrationEmail: params.sendRegistrationEmail.toString()
                } : null
            }
        );
        return response.parsedResponse;
    }

    public async attendeeBulkUpdate(params: IEventRequestParams & IAttendeeBulkUpdateDTO): Promise<JobDtoType> {
        const response = await this.apiClient.put<JobDtoType>(
            {
                url: ATTENDEE_API.BULK_UPDATE_ATTENDEE_URL,
                permission: Permission.UPDATE_ATTENDEE
            },
            params
        );
        return response.parsedResponse;
    }

    public async createAttendee(params: ICreateAttendeeDTO & IEventRequestParams): Promise<IAttendeeDetailsDTO> {
        const response = await this.apiClient.post<IAttendeeDetailsDTO>(
            {
                url: ATTENDEE_API.CREATE_ATTENDEE_URL,
                permission: Permission.CREATE_ATTENDEE
            },
            params
        );
        return response.parsedResponse;
    }

    public async generateAccessCodes(params: IEventRequestParams) {
        const response = await this.apiClient.put<JobDtoType>(ATTENDEE_API.ATTENDEES_GENERATE_ACCESS_CODES, params);
        return response.parsedResponse;
    }

    public async deleteAttendee(params: IDeleteAttendeeDTO) {
        const response = await this.apiClient.delete<JobDtoType>(
            {
                url: ATTENDEE_API.UPDATE_ATTENDEE_URL,
                permission: Permission.DELETE_ATTENDEE
            },
            params
        );
        return response.parsedResponse;
    }

    public async getParticipants(params: IEventRequestParams & ISortRequestParams<TGetAttendeesSortKeys>): Promise<IAttendeesListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);

        const response = await this.apiClient.get<IAttendeesListDTO>(ATTENDEE_API.ATTENDEES_LIST_URL, {
            ...paramsWithSortString,
            size: 10_000_000 // to avoid paging
        });
        return response.parsedResponse;
    }

    public async importAccessCodes({accountId, eventId, file}: IFileParams) {
        const response = await this.apiClient.post<JobDtoType>(ATTENDEE_API.IMPORT_ACCESS_CODES_URL, {
            accountId: accountId,
            eventId: eventId,
            file: file,
            separator: ','
        }, {
            ContentType: RequestContentType.formData,
        });
        return response.parsedResponse;
    }

    public async generateOrganizationsLinks(params: IEventRequestParams) {
        const response = await this.apiClient.put<JobDtoType>(ATTENDEE_API.GENERATE_PRESENTER_LINKS, params);
        return response.parsedResponse;
    }

    public async updateBiography(params: IEventRequestParams & { attendeeId: number }): Promise<IAttendeeDetailsDTO> {
        const response = await this.apiClient.put<IAttendeeDetailsDTO>(ATTENDEE_API.UPDATE_BIOGRAPHY_URL, params);
        return response.parsedResponse;
    }
}

export { AttendeesDAO };
